/**
 * View/Download links for generated report documents (PDF, HTML page, HTML archive).
 * Shows the newest finished row per output format; hidden entirely while nothing has finished yet.
 */

import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Eye, Download } from "lucide-react";
import { useDocumentGeneratedDocuments } from "../hooks/useDocuments";
import { REPORT_FORMATS, selectLatestReports, type ReportFormat } from "../lib/generatedReports";

const FORMAT_LABEL_KEYS: Record<ReportFormat, string> = {
  pdf: "documentDetail.report.pdf",
  html_page: "documentDetail.report.htmlPage",
  html_bundle: "documentDetail.report.htmlBundle",
};

interface GeneratedReportLinksProps {
  projectId: string | undefined;
  documentId: string | undefined;
}

export function GeneratedReportLinks({ projectId, documentId }: GeneratedReportLinksProps) {
  const { t } = useTranslation();
  const { data, isError } = useDocumentGeneratedDocuments(projectId, documentId);

  useEffect(() => {
    if (isError) {
      console.error("Failed to load generated report links", { documentId });
    }
  }, [isError, documentId]);

  const reports = useMemo(() => selectLatestReports(data?.documents ?? []), [data]);
  const hasReports = REPORT_FORMATS.some((format) => reports[format]);

  if (!hasReports && !isError) {
    return null;
  }

  return (
    <div className="mb-6">
      <h3 className="text-sm font-medium text-foreground mb-2">{t("documentDetail.report.title")}</h3>
      {isError ? (
        <p className="text-sm text-destructive">{t("documentDetail.report.loadError")}</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {REPORT_FORMATS.map((format) => {
            const item = reports[format];
            if (!item) {
              return null;
            }
            return (
              <div key={format} className="inline-flex items-center gap-1">
                <span className="text-sm text-muted-foreground mr-1">{t(FORMAT_LABEL_KEYS[format])}</span>
                <a
                  href={item.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-muted hover:bg-accent rounded-lg text-sm text-foreground transition-colors"
                >
                  <Eye className="h-4 w-4" />
                  {t("documentDetail.report.view")}
                </a>
                {item.attachment_url && (
                  <a
                    href={item.attachment_url}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-muted hover:bg-accent rounded-lg text-sm text-foreground transition-colors"
                  >
                    <Download className="h-4 w-4" />
                    {t("documentDetail.report.download")}
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
